import { classNames } from "@/lib/classnames";
import { simpleRenderToString } from "@/lib/html";
import { useEffect, useRef, useCallback, useState } from "react";
import YAModal from "../global/modal";
import { translate } from "@/lib/translator";
import YALabel from "../inputs/label";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  HeartIcon,
  MinusIcon,
  PaperClipIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";

const PrintPreviewIframe = ({ isOpen, setIsOpen, hasModal, children, onContentChange, hasActionButtons }) => {
  const iframeRef = useRef(null);
  const isLoadedRef = useRef(false);
  const [renderedHtml, setRenderedHtml] = useState("");
  const timeoutRef = useRef(null);
  const observerRef = useRef(null);
  const [fontSize, setFontSize] = useState(16);
  const referenceDpi = 96;

  const updateIframeContent = useCallback(() => {
    if (!isLoadedRef.current || !iframeRef.current || !renderedHtml) return;

    const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    if (!iframeDocument) return;

    let styleElement = iframeDocument.getElementById("dynamic-font-style");
    if (!styleElement) {
      // Create a new style element if it doesn't exist
      styleElement = iframeDocument.createElement("style");
      styleElement.id = "dynamic-font-style"; // Set an ID for easy reference
      iframeDocument.head.appendChild(styleElement);
    }
    // Update the font size in the existing style element
    styleElement.textContent = `:root { font-size: ${fontSize}px; }`;

    iframeDocument.body.innerHTML = renderedHtml;

    // Optional scroll to top
    iframeDocument.defaultView?.scrollTo(0, 0);
  }, [renderedHtml, onContentChange]);

  const updateIframeFontSize = useCallback(() => {
    if (!isLoadedRef.current || !iframeRef.current || !renderedHtml) return;

    const iframeDocument = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
    if (!iframeDocument) return;

    let styleElement = iframeDocument.getElementById("dynamic-font-style");
    if (!styleElement) {
      // Create a new style element if it doesn't exist
      styleElement = iframeDocument.createElement("style");
      styleElement.id = "dynamic-font-style"; // Set an ID for easy reference
      iframeDocument.head.appendChild(styleElement);
    }
    // Update the font size in the existing style element
    styleElement.textContent = `:root { font-size: ${fontSize}px; }`;
  }, [fontSize]);

  useEffect(() => {
    let isCancelled = false;

    const render = async () => {
      const html = await simpleRenderToString(children);
      if (!isCancelled) {
        setRenderedHtml(html);
      }
    };

    render();

    return () => {
      isCancelled = true;
    };
  }, [children, isOpen]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const handleLoad = async () => {
      // const html = await simpleRenderToString(children);
      // // setRenderedHtml(html);
      isLoadedRef.current = true;
      // Set up mutation observer
      const iframeDocument = iframeRef.current?.contentDocument;
      if (!iframeDocument) return;
      // iframeDocument.body.innerHTML = html;
      if (!onContentChange) return;

      // Check for existing style element
      
      // Disconnect any existing observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      const observer = new MutationObserver((mutations) => {
        const significantMutations = mutations.filter(
          (m) => m.addedNodes.length > 0 || m.type === "characterData"
        );

        if (significantMutations.length === 0) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
          const iframeBody = iframeDocument.body;
          const divElement = iframeBody.querySelector("div");
          const iframeScrollHeight = divElement ? divElement.scrollHeight : 0;
          console.log(iframeScrollHeight, "scroll height")
          onContentChange(iframeScrollHeight <= 960);
        }, 100);
      });

      observer.observe(iframeDocument.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      observerRef.current = observer;
    };

    iframe.addEventListener("load", handleLoad);
    return () => {
      iframe.removeEventListener("load", handleLoad);
    };
  }, [updateIframeContent]);

  useEffect(() => {
    updateIframeContent();
  }, [renderedHtml, isOpen]);

  useEffect(() => {
    updateIframeFontSize();
  }, [fontSize])

  useEffect(() => {
    if(isOpen === false){
      const iframeDocument = iframeRef.current?.contentDocument;
      if (!iframeDocument) return;
      iframeDocument.body.innerHTML = `<div class="h-[100vh] flex items-center justify-center">
        <svg class="size-5 animate-spin text-ymcagray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
      </div>`;
      setFontSize(16);
    }
  }, [isOpen])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <ModalWrapper isOpen={isOpen ? true : false} setIsOpen={setIsOpen} hasModal={hasModal} iframeRef={iframeRef} hasActionButtons={hasActionButtons} fontSize={fontSize} setFontSize={setFontSize}>
      <div
        style={{
          width: `${referenceDpi * 1 * 8.5}px`,
          height: `100%`,
          overflow: "visible",
          transform: `scale(1)`,
        }}
        className={classNames(
          "origin-top-left preview-container mx-auto"
        )}
      >
        <div
          className="w-full h-auto bg-white shadow-md border relative after:absolute after:pointer-events-none after:inset-x-6 after:top-16 after:bottom-[7.4rem] after:block after:border-2 after:border-dashed after:border-ymcared-700/5"
          id="iframe-print-container"
        >
          <iframe
            ref={iframeRef}
            title="Print Preview"
            width={"100%"}
            height={"500px"}
            style={{ border: "none", overflow: "hidden" }}
            className="aspect-[8.5/11] h-auto"
            srcDoc={`
              <!DOCTYPE html>
              <html>
                <head>
                  <meta charset="UTF-8" />
                  <meta name="viewport" content="width=device-width, initial-scale=1.0" />

                  <link rel="preconnect" href="https://fonts.googleapis.com" />
                  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />

                  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,400;0,700;1,400;1,700&display=swap" />
                  <link rel="stylesheet" href="/print-styles.css" />
                  
                  <style>
                  :root { font-family: 'Open Sans'; }
                  </style>

                  <style id="dynamic-font-style">
                  :root { font-size: 16px; }
                  </style>
                </head>
                <body style="overflow: hidden;">
                  <div class="h-[100vh] flex items-center justify-center">
                    <svg class="size-5 animate-spin text-ymcagray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                  </div>
                </body>
              </html>
            `}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

const ModalWrapper = ({ isOpen, setIsOpen, children, hasModal, iframeRef, hasActionButtons, fontSize, setFontSize }) => {
  if(hasModal === false) return children

  return (
    <YAModal
      unmount={false}
      title={"Print Preview"}
      open={isOpen}
      setOpen={setIsOpen}
      onFormSubmit={(e) => {
        e.preventDefault();
        iframeRef?.current?.contentWindow?.print();
      }}
      submitButtonText={translate("Print")}
      panelClass="max-w-4xl"
      footer={hasActionButtons}
    >
      <div className={classNames(
        "w-full relative", 
        "min-h-[500px]",
      )}>
        {children}
      </div>
      {hasActionButtons && (
        <div className="flex items-center justify-between p-2 mt-4 backdrop-blur-sm bg-ymcagray-200/80 rounded-md -mb-4 sticky bottom-0 bg-white">
          <div>
            <YALabel text={"Font Size"} />
          </div>

          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm [&>a:first-child]:rounded-l-md [&>a:last-child]:rounded-r-md">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (fontSize > 8) {
                    setFontSize(fontSize - 1);
                  }
                }}
                className={classNames(
                  "relative inline-flex items-center px-1.5 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 bg-white focus:z-20 focus:outline-offset-0",
                  fontSize < 9 ? "opacity-50 cursor-not-allowed" : ""
                )}
              >
                <MinusIcon className="h-5 w-5" aria-hidden="true" />
              </a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (fontSize < 30) {
                    setFontSize(fontSize + 1);
                  }
                }}
                className={classNames(
                  "relative inline-flex items-center px-1.5 py-1.5 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 bg-white focus:z-20 focus:outline-offset-0",
                  fontSize > 29 ? "opacity-50 cursor-not-allowed" : ""
                )}
              >
                <PlusIcon className="h-5 w-5" aria-hidden="true" />
              </a>
            </nav>
          </div>
        </div>
      )}
    </YAModal>
  )
}

export default PrintPreviewIframe;
